import React, { useState } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Loader2, MapPin, Phone, Navigation, Clock, User, Building2, FileDown, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import {
  REQUEST_TYPES,
  SERVICE_TYPES,
  URGENCIES,
  ALL_STATUSES,
  TECH_STATUSES,
  STATUS_META,
  URGENCY_META,
  labelOf,
  PROD_SITE_URL,
  waLink,
} from "../../lib/gmaoConstants";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Separator } from "../ui/separator";
import StatusBadge from "./StatusBadge";
import PhotoThumb from "./PhotoThumb";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const fmt = (iso) => {
  try {
    return format(new Date(iso), "dd/MM/yyyy à HH:mm", { locale: fr });
  } catch {
    return iso || "-";
  }
};

const Row = ({ icon: Icon, label, value }) =>
  value ? (
    <div className="flex items-start gap-2 text-sm">
      {Icon && <Icon className="w-4 h-4 text-[hsl(207,78%,42%)] mt-0.5 flex-shrink-0" />}
      <span className="text-muted-foreground">{label} :</span>
      <span className="font-medium text-slate-800">{value}</span>
    </div>
  ) : null;

const InterventionDetail = ({
  open,
  onOpenChange,
  intervention,
  role,
  technicians = [],
  onUpdated,
}) => {
  const [statusValue, setStatusValue] = useState("");
  const [note, setNote] = useState("");
  const [techValue, setTechValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [clientNotifType, setClientNotifType] = useState("intervention_programmee");
  const [notifying, setNotifying] = useState(false);

  if (!intervention) return null;

  const isAdmin = role === "admin";
  const isTech = role === "technicien";
  const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    `${intervention.address} ${intervention.postalCode || ""} ${intervention.city || ""}`
  )}`;

  const assignedTech = technicians.find((t) => t.id === intervention.assignedTechnicianId);

  const techWaMessage = () =>
    `Bonjour, une intervention vient de vous être affectée.\n` +
    `Dossier : ${intervention.dossierNumber}\n` +
    `Adresse : ${intervention.address} ${intervention.postalCode || ""} ${intervention.city || ""}\n` +
    `Client : ${intervention.clientName || intervention.tenantName || "-"}\n` +
    `Type : ${labelOf(SERVICE_TYPES, intervention.serviceType)}\n` +
    `Urgence : ${labelOf(URGENCIES, intervention.urgency)}\n` +
    `Créneau : ${intervention.preferredSlot || "à convenir"}\n` +
    `Lien intervention : ${PROD_SITE_URL}/technicien\n` +
    `Merci de confirmer la prise en charge.`;

  const clientWaMessage = () => {
    const labels = {
      intervention_programmee: "votre intervention est programmée",
      technicien_affecte: "un technicien a été affecté à votre intervention",
      technicien_en_route: "votre technicien est en route",
      bon_disponible: "votre bon d'intervention signé est disponible",
      facture_disponible: "votre facture est disponible",
      demande_info: "nous avons besoin d'une information complémentaire",
    };
    return `Bonjour, ${labels[clientNotifType]} (dossier ${intervention.dossierNumber}). ` +
      `Votre espace : ${PROD_SITE_URL}/login\nNGL SERVICES`;
  };

  const notifyClientEmail = async () => {
    setNotifying(true);
    try {
      await API.post(`/interventions/${intervention.id}/notify-client`, { type: clientNotifType });
      toast.success("Notification email envoyée au client");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setNotifying(false);
    }
  };

  const downloadReportPdf = async () => {
    try {
      const res = await API.get(`/interventions/${intervention.id}/report-pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch {
      toast.error("Bon d'intervention indisponible.");
    }
  };

  const applyStatus = async (newStatus) => {
    setLoading(true);
    try {
      const { data } = await API.patch(`/interventions/${intervention.id}/status`, {
        status: newStatus,
        note: note || null,
      });
      toast.success("Statut mis à jour");
      setNote("");
      setStatusValue("");
      onUpdated?.(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const assignTech = async (techId) => {
    setLoading(true);
    try {
      const { data } = await API.patch(`/interventions/${intervention.id}`, {
        assignedTechnicianId: techId,
      });
      toast.success("Technicien affecté");
      setTechValue("");
      onUpdated?.(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const urgencyMeta = URGENCY_META[intervention.urgency] || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="intervention-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 flex-wrap">
            <span className="font-mono text-[hsl(207,78%,42%)]">{intervention.dossierNumber}</span>
            {intervention.bonNumber && <span className="font-mono text-xs text-slate-500">Bon {intervention.bonNumber}</span>}
            <StatusBadge status={intervention.status} />
            <span className={`text-xs px-2 py-0.5 rounded ${urgencyMeta.color || ""}`}>
              {urgencyMeta.label || intervention.urgency}
            </span>
          </DialogTitle>
          <DialogDescription>Détail de l'intervention, affectation, statut et historique.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Infos demande */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Row label="Type" value={labelOf(REQUEST_TYPES, intervention.requestType)} />
            <Row label="Service" value={labelOf(SERVICE_TYPES, intervention.serviceType)} />
            <Row icon={Clock} label="Créneau souhaité" value={intervention.preferredSlot} />
            <Row label="Créée le" value={fmt(intervention.createdAt)} />
          </div>

          <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-700">
            {intervention.description}
          </div>

          <Separator />

          {/* Localisation */}
          <div className="space-y-1">
            <Row icon={MapPin} label="Adresse" value={`${intervention.address} ${intervention.postalCode || ""} ${intervention.city || ""}`} />
            <Row label="Étage" value={intervention.floor} />
            <Row label="Digicode" value={intervention.digicode} />
            <Row label="Accès" value={intervention.accessInfo} />
            <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" className="mt-2" data-testid="detail-gps-button">
                <Navigation className="w-4 h-4 mr-2" /> Itinéraire GPS
              </Button>
            </a>
          </div>

          {/* Client / locataire */}
          {(intervention.clientName || intervention.tenantName) && (
            <>
              <Separator />
              <div className="space-y-1">
                <Row icon={User} label="Client" value={intervention.clientName} />
                <Row icon={Building2} label="Société" value={intervention.clientCompany} />
                <Row icon={User} label="Locataire" value={intervention.tenantName} />
                {intervention.tenantPhone && (
                  <a href={`tel:${intervention.tenantPhone}`}>
                    <Button variant="outline" size="sm" className="mt-1" data-testid="detail-call-tenant">
                      <Phone className="w-4 h-4 mr-2" /> Appeler le locataire
                    </Button>
                  </a>
                )}
              </div>
            </>
          )}

          {/* Technicien affecté */}
          {intervention.assignedTechnicianName && (
            <Row label="Technicien affecté" value={intervention.assignedTechnicianName} />
          )}

          {/* Photos */}
          {(intervention.photos || []).length > 0 && (
            <>
              <Separator />
              <div>
                <p className="font-semibold text-sm text-slate-800 mb-2">Photos ({intervention.photos.length})</p>
                <div className="grid grid-cols-4 gap-2">
                  {intervention.photos.map((p) => (
                    <div key={p.id} className="relative">
                      <PhotoThumb interventionId={intervention.id} photo={p} className="w-full h-20 rounded-lg border" />
                      <span className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[9px] text-center rounded-b-lg">{p.category}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Rapport */}
          {intervention.report && (
            <>
              <Separator />
              <div className="text-sm space-y-1">
                <p className="font-semibold text-slate-800 mb-1">Rapport d'intervention</p>
                {intervention.report.workDone && <p><span className="text-muted-foreground">Travail réalisé :</span> {intervention.report.workDone}</p>}
                {intervention.report.materialUsed && <p><span className="text-muted-foreground">Matériel :</span> {intervention.report.materialUsed}</p>}
                {intervention.report.difficulty && <p><span className="text-muted-foreground">Difficulté :</span> {intervention.report.difficulty}</p>}
                {intervention.report.partToOrder && <p><span className="text-muted-foreground">Pièce à commander :</span> {intervention.report.partToOrder}</p>}
                {intervention.report.clientObservation && <p><span className="text-muted-foreground">Observation client :</span> {intervention.report.clientObservation}</p>}
                <p><span className="text-muted-foreground">Terminée :</span> {intervention.report.completed ? "Oui" : "Non"}</p>
                {!intervention.report.completed && intervention.report.nextAction && <p><span className="text-muted-foreground">Prochaine action :</span> {intervention.report.nextAction}</p>}
              </div>
            </>
          )}

          {/* Signature */}
          {intervention.signature && (
            <div className="bg-emerald-50 rounded-lg p-3 text-sm">
              Signé par <strong>{intervention.signature.signerName}</strong> le {(intervention.signature.at || "").slice(0, 16).replace("T", " ")}
            </div>
          )}

          {/* Bon d'intervention PDF */}
          {(intervention.report || intervention.signature || (intervention.photos || []).length > 0) && (
            <div className="space-y-1">
              <Button variant="outline" size="sm" onClick={downloadReportPdf} data-testid="detail-report-pdf">
                <FileDown className="w-4 h-4 mr-2" /> Bon d'intervention (PDF)
              </Button>
              {isAdmin && intervention.bonDownloadedAt && (
                <p className="text-xs text-emerald-700">✓ Consulté par le client le {(intervention.bonDownloadedAt || "").slice(0, 16).replace("T", " ")}</p>
              )}
            </div>
          )}

          {/* ACTIONS ADMIN */}
          {isAdmin && (
            <>
              <Separator />
              <div className="space-y-3 bg-blue-50 rounded-lg p-4">
                <p className="font-semibold text-sm text-slate-800">Actions administrateur</p>

                <div className="space-y-2">
                  <Label className="text-xs">Affecter un technicien</Label>
                  <div className="flex gap-2">
                    <Select value={techValue} onValueChange={setTechValue}>
                      <SelectTrigger data-testid="assign-tech-select"><SelectValue placeholder="Choisir un technicien" /></SelectTrigger>
                      <SelectContent>
                        {technicians.map((t) => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button disabled={!techValue || loading} onClick={() => assignTech(techValue)} data-testid="assign-tech-button">
                      Affecter
                    </Button>
                  </div>
                  {assignedTech && (
                    <a
                      href={assignedTech.phone ? waLink(assignedTech.phone, techWaMessage()) : undefined}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => { if (!assignedTech.phone) { e.preventDefault(); toast.error("Ce technicien n'a pas de numéro de téléphone."); } }}
                    >
                      <Button variant="outline" size="sm" className="mt-1 border-green-500 text-green-700 hover:bg-green-50" data-testid="notify-tech-whatsapp">
                        <MessageCircle className="w-4 h-4 mr-2" /> Notifier {assignedTech.name} par WhatsApp
                      </Button>
                    </a>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-xs">Changer le statut</Label>
                  <Input
                    placeholder="Note (optionnel)"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className="mb-2"
                    data-testid="status-note-input"
                  />
                  <div className="flex gap-2">
                    <Select value={statusValue} onValueChange={setStatusValue}>
                      <SelectTrigger data-testid="status-change-select"><SelectValue placeholder="Nouveau statut" /></SelectTrigger>
                      <SelectContent>
                        {ALL_STATUSES.map((s) => (
                          <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button disabled={!statusValue || loading} onClick={() => applyStatus(statusValue)} data-testid="apply-status-button">
                      {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Appliquer
                    </Button>
                  </div>
                </div>

                <div className="space-y-2 border-t border-blue-200 pt-3">
                  <Label className="text-xs">Notifier le client / l'agence</Label>
                  <Select value={clientNotifType} onValueChange={setClientNotifType}>
                    <SelectTrigger data-testid="client-notif-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="intervention_programmee">Intervention programmée</SelectItem>
                      <SelectItem value="technicien_affecte">Technicien affecté</SelectItem>
                      <SelectItem value="technicien_en_route">Technicien en route</SelectItem>
                      <SelectItem value="bon_disponible">Bon d'intervention disponible</SelectItem>
                      <SelectItem value="facture_disponible">Facture disponible</SelectItem>
                      <SelectItem value="demande_info">Demande d'information</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={notifying} onClick={notifyClientEmail} data-testid="notify-client-email">
                      {notifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Email
                    </Button>
                    {(intervention.clientPhone || intervention.tenantPhone) && (
                      <a href={waLink(intervention.clientPhone || intervention.tenantPhone, clientWaMessage())} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" size="sm" className="border-green-500 text-green-700 hover:bg-green-50" data-testid="notify-client-whatsapp">
                          <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ACTIONS TECHNICIEN */}
          {isTech && (
            <>
              <Separator />
              <div className="space-y-3 bg-violet-50 rounded-lg p-4">
                <p className="font-semibold text-sm text-slate-800">Mettre à jour le statut</p>
                <div className="flex flex-wrap gap-2">
                  {TECH_STATUSES.map((s) => (
                    <Button
                      key={s.value}
                      variant={intervention.status === s.value ? "default" : "outline"}
                      size="sm"
                      disabled={loading}
                      onClick={() => applyStatus(s.value)}
                      data-testid={`tech-status-${s.value}`}
                    >
                      {s.label}
                    </Button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Historique */}
          <Separator />
          <div>
            <p className="font-semibold text-sm text-slate-800 mb-2">Historique</p>
            <div className="space-y-2">
              {(intervention.statusHistory || []).slice().reverse().map((h, i) => (
                <div key={i} className="flex items-start gap-2 text-xs">
                  <span className="text-muted-foreground whitespace-nowrap">{fmt(h.at)}</span>
                  <StatusBadge status={h.status} />
                  {h.note && <span className="text-slate-600">— {h.note}</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InterventionDetail;
